import { usePublishEvent } from '@/analytics/usePublishEvent';
import { notifyError } from '@/common/utils/notifyError';
import Image from 'next/image';
import Script from 'next/script';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import { AppleLogo } from '../../Svg';
import styles from '../social-signin.module.scss';
import { SocialAlert, useSocialAlerts } from '../SocialAlertsContext';
import { SocialStyledPrimaryButton } from '../SocialButton/SocialButton';
import { useSocialReducer } from '../useSocialReducer';
import { AuthorizationResponse } from './AppleButtonTypes';

declare global {
  interface Window {
    AppleID: AppleSignInAPI.AppleID;
  }
}

type AppleButtonProps = {
  disabled?: boolean;
  loading: boolean;
  onAuthorizationResponse: (
    response: AuthorizationResponse,
    nonce: string,
  ) => void;
};

const appleUrl =
  'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';

const expectedPopupFailures = [
  'popup_closed_by_user',
  'user_trigger_new_signin_flow',
  'popup_blocked_by_browser',
];

export const AppleButton = ({
  disabled,
  loading,
  onAuthorizationResponse,
}: AppleButtonProps) => {
  const { t } = useTranslation();
  const publishEvent = usePublishEvent();
  const setSocialAlert = useSocialAlerts();

  const [nonce] = useState(uuidv4());

  const { scrState, dispatch } = useSocialReducer({
    loaded:
      typeof window !== 'undefined' && typeof window.AppleID !== 'undefined',
    failed: false,
  });

  const handleScriptError = () => {
    publishEvent('AppleSignInFailedScript', 'apple-login');
    dispatch({ type: 'failed' });
  };

  const buttonDisabled = disabled || (!scrState.loaded && !scrState.failed);

  useEffect(() => {
    if (scrState.loaded) {
      try {
        window.AppleID.auth.init({
          clientId: process.env.APPLE_CLIENT_ID,
          scope: process.env.APPLE_OIDC_SCOPES,
          redirectURI: process.env.APPLE_REDIRECT_URI || `${window.origin}/`,
          nonce: nonce,
          state: 'origin:web',
          usePopup: true, // required to get data client side.
        });
      } catch (error) {
        notifyError(error);
        handleScriptError();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrState.loaded]);

  const onAppleClick = async () => {
    if (scrState.failed) {
      setSocialAlert(SocialAlert.APPLE);
      return;
    }

    try {
      const response = await window.AppleID.auth.signIn();
      onAuthorizationResponse(response, nonce);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (failure: any) {
      /* istanbul ignore next */
      if (!('error' in failure)) {
        notifyError(failure);
      } else if (!expectedPopupFailures.includes(failure.error)) {
        notifyError(failure.error);
      }
    }
  };

  return (
    <>
      <Script
        src={appleUrl}
        onLoad={() => dispatch({ type: 'loaded' })}
        onError={handleScriptError}
      />
      <div className={styles.btn_container}>
        <SocialStyledPrimaryButton
          className={styles.placeholder_btn}
          disabled={buttonDisabled}
          id="apple-continue-button"
          data-testid="apple-continue-button"
          onClick={onAppleClick}
        >
          <div className={styles.overlay_wrapper}>
            <div className={styles.logo_wrapper}>
              <AppleLogo disabled={buttonDisabled} />
            </div>
            <div
              className={styles.button_text}
            >{`${t('Continue with Apple')}`}</div>
          </div>
          <div className={styles.loading_wrapper}>
            {loading && (
              <Image
                src={`${process.env.ASSET_PREFIX}/images/circle-loader.svg`}
                alt="button-loader"
                data-testid="button-loader"
                height={24}
                width={24}
                className={styles.loading_image}
              />
            )}
          </div>
        </SocialStyledPrimaryButton>
      </div>
    </>
  );
};
